import { useState } from 'react';
import api from '../lib/api';
import { Section, Spinner, ErrorNote, Empty, useAsync } from './ui';
import RichText from './RichText';
import RichTextEditor from './RichTextEditor';

// Author-side notification panel (design/016). Drop into a course or group
// page; posts to that scope and lists existing posts with delete. Gated by the
// caller (only render for publishers / the group's leader).
export default function NotificationComposer({ scopeType, scopeId, scopeName }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const { loading, error, data, reload } = useAsync(
    () => api.notificationsForScope(scopeType, scopeId).catch(() => []),
    [scopeType, scopeId],
  );

  const post = async () => {
    if (!title.trim()) { alert('Title is required.'); return; }
    if (!body || !body.replace(/<[^>]+>/g, '').trim()) { alert('Message is required.'); return; }
    setSaving(true);
    try {
      await api.notificationCreate({ scopeType, scopeId, title: title.trim(), body });
      setTitle(''); setBody('');
      reload();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const del = async (n) => {
    if (!window.confirm(`Delete notification "${n.title}"?`)) return;
    try { await api.notificationDelete(n.id); reload(); } catch (e) { alert(e.message); }
  };

  return (
    <Section title="Notifications">
      <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Post an announcement to everyone in <b>{scopeName}</b>. They'll see it on their
        Notifications page and receive an email.
      </p>

      <div className="form-field"><label>Title <span className="req">*</span></label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Announcement title…" /></div>
      <div className="form-field"><label>Message <span className="req">*</span></label>
        <RichTextEditor value={body} onChange={setBody} placeholder="Write your announcement…" /></div>
      <div>
        <button className="btn btn-primary" disabled={saving} onClick={post}>{saving ? 'Posting…' : 'Post notification'}</button>
      </div>

      <div style={{ marginTop: 18, borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 14 }}>
        <div className="section-title" style={{ marginBottom: 10 }}>Posted</div>
        {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
          (!data || data.length === 0) ? <Empty>No notifications posted yet.</Empty> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.map((n) => (
                <div key={n.id} style={{
                  background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: 'var(--radius-sm, 8px)', padding: '12px 14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <b style={{ flex: 1 }}>{n.title}</b>
                    <span style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>
                      {n.createdAt ? new Date(n.createdAt).toLocaleDateString() : ''}
                    </span>
                    <button className="btn btn-sm btn-danger" onClick={() => del(n)}>Delete</button>
                  </div>
                  <div style={{ marginTop: 6, fontSize: '0.875rem', color: 'var(--text)' }}><RichText html={n.body} /></div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </Section>
  );
}
